import Manager from 'coreClasses/Manager';
import PWAException from 'coreClasses/PWAException';
import OrderModel from '../order/OrderModel';
import ReturnReasonModel from './ReturnReasonModel';
import {IPrevalidatePayload, IReturnAuthorizationPayload} from './IReturnOrderData';

/**
 * Represents ReturnAuthorizationMgr
 */
class ReturnAuthorizationMgr extends Manager {
	/**
	 * Makes request to create ReturnAuthorization CO
	 * @param {IReturnAuthorizationPayload} payload - payload
	 */
	async createReturnAuthorization(payload: IReturnAuthorizationPayload) {
		try {
			const response = await this.managerRepository
				.getCommerceApi()
				.customShopperReturnAuthorizations
				.createReturnAuthorization({
					body: payload
				}, true);

			this.managerRepository.getOrdersMgr().cleanupOrders();

			return response;
		} catch (e) {
			if (e.name && e.message) {
				throw new PWAException(e.name, e.message);
			}

			throw e;
		}
	}

	/**
	 * Handles ReturnAuthorization process for guests
	 * @param {Object} payload - payload
	 * @param {String} payload.orderNo - orderNo
	 * @param {String} payload.email - email
	 * @returns {Promise<OrderModel>} the order model or null if not existing
	 */
	async getGuestReturnAuthorizationOrder({orderNo, email}): Promise<OrderModel> {
		try {
			const response = await this.managerRepository.getCommerceApi().customShopperOrders.getOrderByEmail({
				pathParameters: {
					c_orderNo: orderNo
				},
				parameters: {
					c_email: email
				}
			}, true);

			return new OrderModel(this.managerRepository, response.order);
		} catch (e) {
			throw new PWAException(null, e.message);
		}
	}

	/**
	 * Provides return reasons array
	 */
	async getReturnReasons() {
		const {returnReasons} = await this.managerRepository
			.getCommerceApi()
			.customShopperReturnAuthorizations
			.getReturnAuthorizationReasons({});

		return returnReasons.map((returnReason) => new ReturnReasonModel(returnReason));
	}

	/**
	 * Prevalidates a Return Authorization Request
	 * @param {IPrevalidatePayload} payload
	 */
	async prevalidateReturnAuthorization(payload: IPrevalidatePayload) {
		try {
			return await this.managerRepository
				.getCommerceApi()
				.customShopperReturnAuthorizations
				.preValidateReturnAuthorization({
					body: payload
				}, true);
		} catch (e) {
			if (e.name && e.message) {
				throw new PWAException(e.name, e.message);
			}

			throw e;
		}
	}
}

export default ReturnAuthorizationMgr;
